import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert, Box, Grid, TextField, Button, Typography, Stack,
  MenuItem, CircularProgress
} from "@mui/material";
import useFormValidation from "hooks/useFormValidation";
import { validators } from "utils/formValidation";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ImageIcon from "@mui/icons-material/Image";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MainCard from "components/MainCard";
import api, { resolveAssetUrl } from "utils/api";

export default function ProductSubCategoryForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name_en: "",
    name_ar: "",
    product_category_id: "",
    image: null
  });
  const [preview, setPreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const { formError, validate, setServerError, clearFieldError, fieldErrorProps } = useFormValidation();
  const [loadingCategories, setLoadingCategories] = useState(true);

  
  useEffect(() => {
    const init = async () => {
      await fetchCategories();
      if (isEdit) await fetchSubCategory();
    };
    init();
  }, [id]);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await api.get("/product/category/list");
      setCategories(res.data.data || []);
    } catch (error) {
      console.error("Failed to load categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchSubCategory = async () => {
    try {
      const res = await api.get(`/product/sub/category/${id}`);
      const data = res.data.data;

     
      const catId = String(data.product_category_id || data.category?.id || "");

      setForm({
        name_en: data.name_en || "",
        name_ar: data.name_ar || "",
        product_category_id: catId,
        image: null
      });

      if (data.imageDoc?.attachment_url) {
        setPreview(resolveAssetUrl(data.imageDoc.attachment_url));
      } else if (data.image) {
        setPreview(resolveAssetUrl(data.image));
      }
    } catch (error) {
      console.error("Failed to fetch sub-category:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name) clearFieldError(name);
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm(prev => ({ ...prev, image: file }));
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(
      {
        product_category_id: [validators.required("Category")],
        name_en: [validators.required("Name (English)")]
      },
      form
    )) {
      return;
    }
    try {
      const formData = new FormData();
      formData.append("name_en", form.name_en);
      formData.append("name_ar", form.name_ar);
      formData.append("product_category_id", form.product_category_id);
      if (form.image) formData.append("image", form.image);

      if (isEdit) {
        await api.put(`/product/sub/category/edit/${id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      } else {
        await api.post("/product/sub/category/add", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      navigate("/product/sub-category/list");
    } catch (error) {
      setServerError(error.response?.data?.message || "Failed to save sub category");
    }
  };

  const getCatId = (cat) => String(cat.id || cat.product_category_id || "");

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>
      </Stack>

      {formError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formError}
        </Alert>
      ) : null}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MainCard title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ImageIcon color="primary" />
                <Typography variant="h6">
                  {isEdit ? "Edit" : "Create"} Product Sub Category
                </Typography>
              </Box>
            }>
              <Grid container spacing={3}>

                {/* Category Dropdown */}
                <Grid item xs={12} md={4}>
                  <TextField
                    select fullWidth required
                    label="Category"
                    name="product_category_id"
                    value={form.product_category_id}
                    onChange={handleChange}
                    disabled={loadingCategories}
                    {...fieldErrorProps("product_category_id")}
                    helperText={fieldErrorProps("product_category_id").helperText || (loadingCategories ? "Loading categories..." : undefined)}
                  >
                    <MenuItem value=""><em>Select Category</em></MenuItem>
                    {categories.map(cat => (
                      // KEY FIX: use getCatId() which reads cat.id (what the API actually returns)
                      <MenuItem key={getCatId(cat)} value={getCatId(cat)}>
                        {cat.name_en}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Name English */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth required
                    label="Name English"
                    name="name_en"
                    value={form.name_en}
                    onChange={handleChange}
                    {...fieldErrorProps("name_en")}
                  />
                </Grid>

                {/* Name Arabic */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Name Arabic"
                    name="name_ar"
                    value={form.name_ar}
                    onChange={handleChange}
                  />
                </Grid>

                {/* Image Upload */}
                <Grid item xs={12} md={6}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<CloudUploadIcon />}
                  >
                    {form.image ? form.image.name : "Upload Image"}
                    <input type="file" name="image" hidden accept="image/*" onChange={handleFile} />
                  </Button>
                  {preview && (
                    <img
                      src={preview}
                      alt="Preview"
                      style={{ marginTop: 10, height: 80, borderRadius: 4, objectFit: "cover" }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Leave empty to use default image
                  </Typography>
                </Grid>

              </Grid>
            </MainCard>
          </Grid>

          {/* Submit */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loadingCategories}
              >
                {isEdit ? "Update Sub Category" : "Create Sub Category"}
              </Button>
            </Box>
          </Grid>

        </Grid>
      </form>
    </Box>
  );
}